'use client';
import Image from 'next/image';
import { useEffect } from 'react';
import { env } from 'src/env.mjs';
import {
    AppleAuthenticationRequest,
    AppleAuthenticationResponse,
    useLoginWithApple,
} from 'src/hooks/User/useLogin';

type AppleLoginButtonProps = {
    quizToken?: string;
    navigate?: boolean;
    source?: string;
};

declare global {
    interface AppleIDAuth {
        init: (config: {
            clientId: string;
            scope: string;
            redirectURI: string;
            usePopup?: boolean;
        }) => void;
        signIn: () => Promise<AppleAuthenticationResponse>;
    }

    interface Window {
        AppleID: {
            auth: AppleIDAuth;
        };
    }
}

const onSuccess = async (
    response: AppleAuthenticationResponse,
    onLoginSuccess: (data: AppleAuthenticationRequest) => void,
) => {
    const request: AppleAuthenticationRequest = {
        identityToken: response.authorization.id_token,
        authorizationCode: response.authorization.code,
    };

    if (response.user) {
        request.fullName = {
            givenName: response.user.name.firstName,
            familyName: response.user.name.lastName,
        };
    }

    onLoginSuccess(request);
};

function AppleLoginButton({
    quizToken,
    navigate = true,
    source,
}: AppleLoginButtonProps) {
    useEffect(() => {
        const script = document.createElement('script');

        script.src =
            'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        script.type = 'text/javascript';
        script.async = true;
        script.onload = () => {
            window.AppleID.auth.init({
                clientId: env.NEXT_PUBLIC_APPLE_CLIENT_ID,
                scope: 'name email',
                redirectURI: window.location.origin,
                usePopup: true,
            });
        };

        document.body.appendChild(script);
    }, []);

    const { mutateAsync: loginWithApple } = useLoginWithApple({
        navigate,
    });

    const onLoginSuccess = async (data: AppleAuthenticationRequest) => {
        await loginWithApple({
            ...data,
            quizToken,
            source,
        });
    };

    return (
        <>
            <button
                className="font-demi flex h-10 w-full items-center justify-center gap-2 rounded-[4px] border border-[#000] bg-[#000] text-white hover:bg-black"
                type="button"
                onClick={async () => {
                    const data = await window.AppleID.auth.signIn();
                    onSuccess(data, onLoginSuccess);
                }}
            >
                <Image
                    className="pb-1"
                    src="/user/images/icons/apple.png"
                    width={14}
                    height={13}
                    alt="Apple"
                />
                Continue with Apple
            </button>
        </>
    );
}

export default AppleLoginButton;
